import { TaxFeeKind } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { formatPhoneString } from '@avantstay/backoffice-core'

import { WEBSITE_NON_REFUNDABLE_POLICY_ID } from '@new/__global/constants/cancellationPolicy'
import { RESERVATION_TYPE_IDS_ALLOWING_COUPONS } from '@new/domains/bookingsTapeChart/components/CreateBookingForm/CreateBookingForm.constants'
import {
  BookingFreePriceInputProps,
  BookingPriceInputProps,
  CreateBookingActionProps,
  CreateBookingFormProps,
} from '@new/domains/bookingsTapeChart/components/CreateBookingForm/CreateBookingForm.types'
import { createBookingMembershipStore } from '@new/domains/bookingsTapeChart/stores/CreateBookingMembershipStore'

import { ReservationType } from '@/types/servicesTypes/Bookings.types'

export const canApplyCoupon = (reservationTypeId?: string) => {
  return !!reservationTypeId && RESERVATION_TYPE_IDS_ALLOWING_COUPONS.includes(reservationTypeId)
}

export const travelAgentDiscountId = '40b2d1fc-59d9-4aad-b5cf-fe0e70432bdd'

export const getStandardReservationType = () => ({
  id: 'af95e11a-3abd-11ed-8692-0242ac120005',
  name: 'Standard',
})

export const getIsFreeReservationType = (
  reservationTypeOptions?: ReservationType,
  selectedReservationTypeId?: string,
) =>
  selectedReservationTypeId
    ? !!reservationTypeOptions
        ?.filter(option => !option.isRevenueGenerating)
        .find(option => option.id.includes(selectedReservationTypeId))
    : false

export const getFreeReservationInput = ({ nights, taxesAndFees }: BookingFreePriceInputProps) => {
  return {
    taxExemption: false,
    nightsOverrides: nights?.map(night => ({
      date: night.date,
      price: 0,
      discountApplied: night.discountApplied,
    })),
    taxFeeOverrides: taxesAndFees
      ?.filter(taxFee => taxFee.kind === TaxFeeKind.fee)
      .map(({ taxFeeId }) => ({ taxFeeId: taxFeeId ?? '', amount: 0 })),
    securityDeposit: 0,
  }
}

export const validateFormErrorMessages = {
  maxOccupancy:
    "The guest count (adults + children + infants) cannot be greater than the home's max occupancy",
  maxAdults: "The adult guests count cannot be greater than the home's max occupancy",
  maxPets: (maxPetsValue: number) => `Maximum number of pets allowed: ${maxPetsValue}`,
}

export const parseFormFields = (
  formValues: CreateBookingFormProps,
  pricingInput: BookingPriceInputProps,
): CreateBookingActionProps | undefined => {
  const {
    hotelRoomTypeId,
    home,
    reservationType,
    guestCount: { adults, children, infants },
    pets,
    notes,
    guestInfo,
    period,
    isAcquisition,
    leadSource,
    acquisitionType,
    acquisitionPhase,
    partnerBookingId,
    agencyBookingId,
    creationTime,
    couponCode,
    paymentPlanId,
    cancellationPolicyCategoryId,
  } = formValues

  const nights = pricingInput.nights
    .filter(nightItem => !Number.isNaN(nightItem?.price))
    .map(night => ({
      date: night.date,
      price: night?.price as number,
      discountApplied: night.discountApplied,
    }))
  const taxesAndFees = pricingInput.taxesAndFees.map(taxFee => ({
    ...taxFee,
    taxFeeId: taxFee.taxFeeId ?? taxFee.name,
  }))
  const servicesPrices = pricingInput.servicesPrices.map(service => ({
    kind: service.kind,
    fee: service.total !== null ? (service.total ?? 0) : service.fee,
    deposit: service.deposit,
  }))

  const pricing = {
    nights,
    taxesAndFees,
    securityDeposit: pricingInput.deposits.security,
    servicesPrices,
  }

  const formattedPhone = formatPhoneString(guestInfo?.phone)

  const guestName = guestInfo?.fullName
  const guestEmail = guestInfo?.email

  if (
    ((home && home?.id) || hotelRoomTypeId) &&
    period?.fromDate &&
    period?.toDate &&
    reservationType &&
    guestName &&
    guestEmail
  )
    return {
      bookingRequestInfo: {
        hotelRoomTypeId,
        homeId: home?.id,
        from: period?.fromDate,
        until: period?.toDate,
        reservationType,
        adults,
        children,
        infants,
        pets: pets || 0,
        notes,
        pricing,
        isAcquisition,
        leadSource: isAcquisition ? leadSource : undefined,
        acquisitionType: isAcquisition ? acquisitionType : undefined,
        acquisitionPhase: isAcquisition ? acquisitionPhase : undefined,
        partnerBookingId: isAcquisition ? partnerBookingId : undefined,
        agencyBookingId: isAcquisition ? agencyBookingId : undefined,
        creationTime: isAcquisition ? creationTime : undefined,
        couponCode,
        paymentPlanId,
        cancellationPolicyCategoryId,
        membershipPlanId: createBookingMembershipStore.selectedMembershipPlanId ?? undefined,
      },
      guestId: guestInfo?.guestId,
      guestData: {
        fullName: guestName,
        email: guestEmail?.trim(),
        phone: formattedPhone,
      },
    }

  return undefined
}

export const parseValuesToCalculatePriceBreakdown = (values: CreateBookingFormProps) => {
  const { home, hotelRoomTypeId, period, guestCount, pets, guestInfo } = values
  const guests =
    (guestCount?.adults || 1) + (guestCount?.children || 0) + (guestCount?.infants || 0)

  return {
    homeId: home?.id,
    hotelRoomTypeId,
    from: period?.fromDate,
    until: period?.toDate,
    guests,
    pets: pets || 0,
    reservationType: values?.reservationType,
    userId: guestInfo?.userId,
  }
}

export const getDisclaimerMessage = (reservationTypeId?: string) => {
  const standardReservationType = getStandardReservationType()

  if (reservationTypeId?.includes(standardReservationType.id)) {
    return 'After creating this reservation, dates will get blocked, payment link and all booking communications will be sent to the guest.'
  }

  return 'After creating this reservation, dates will get blocked.'
}

export const recalculatePriceByReservationType = (
  isRevenueGeneratingReservationType: boolean,
  pricingInput?: BookingPriceInputProps,
) => {
  const nights = pricingInput?.nights
  const taxesAndFees = pricingInput?.taxesAndFees

  if (!isRevenueGeneratingReservationType && nights && taxesAndFees) {
    return {
      reservationPriceInput: getFreeReservationInput({ nights, taxesAndFees }),
      isBookingFree: true,
    }
  }

  return {
    reservationPriceInput: undefined,
    isBookingFree: false,
  }
}

export const checkIsMissingNightsPrices = (bookingPriceBreakdown?: BookingPriceInputProps) => {
  return !bookingPriceBreakdown?.nights.every(night => night.price !== undefined)
}

export const isNonRefundableCancellationPolicy = (cancellationPolicy?: string) => {
  return cancellationPolicy === WEBSITE_NON_REFUNDABLE_POLICY_ID
}
